import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import ReceiptCameraCapture from '../../components/kiosk/ReceiptCameraCapture';
import { salesExpenseService } from '../../services/salesExpenseService';
import {
  SalesCard,
  SalesPageHeader,
  SALES,
  salesPrimaryButtonSx,
} from '../../components/sales/salesUi';

const SalesExpenseNew = () => {
  const navigate = useNavigate();
  const [photoFile, setPhotoFile] = useState(null);
  const [notes, setNotes] = useState('');
  const [amount, setAmount] = useState('');
  const [receiptDate, setReceiptDate] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setError('');
  }, [photoFile, notes, amount, receiptDate]);

  const parseAmountInput = (raw) => {
    const n = parseFloat(String(raw ?? '').replace(/[$,\s]/g, '').trim());
    return Number.isFinite(n) ? n : NaN;
  };

  const amountValue = parseAmountInput(amount);
  const amountValid = Number.isFinite(amountValue) && amountValue > 0;
  const notesValid = notes.trim().length > 0;
  const canSubmit = Boolean(photoFile) && amountValid && notesValid && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await salesExpenseService.submit({
        file: photoFile,
        notes: notes.trim(),
        amount: amountValue,
        receiptDate: receiptDate || undefined,
      });
      const id = res?.data?.id;
      navigate(id ? `/sales/expenses/${id}` : '/sales/expenses');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to submit expense');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      <SalesPageHeader
        title="New Expense"
        subtitle="Take a photo of your receipt and enter amount and job/notes."
        actions={
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/sales/expenses')}
            sx={{ textTransform: 'none', color: SALES.ink }}
          >
            Back to expenses
          </Button>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <SalesCard>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Take a photo of your receipt or choose an image from your device.
        </Typography>
        <ReceiptCameraCapture onCapture={setPhotoFile} disabled={submitting} />
        <TextField
          fullWidth
          size="small"
          label="Receipt date (optional)"
          type="date"
          value={receiptDate}
          onChange={(e) => setReceiptDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ mt: 2, mb: 2 }}
        />
        <TextField
          fullWidth
          size="small"
          required
          label="Amount"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          error={amount.trim() !== '' && !amountValid}
          helperText={
            amount.trim() !== '' && !amountValid
              ? 'Enter an amount greater than 0'
              : undefined
          }
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          size="small"
          required
          label="Job/Notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          multiline
          rows={3}
          sx={{ mb: 2 }}
        />
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button
            onClick={() => navigate('/sales/expenses')}
            disabled={submitting}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!canSubmit}
            onClick={handleSubmit}
            sx={salesPrimaryButtonSx}
          >
            {submitting ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              'Submit expense'
            )}
          </Button>
        </Box>
      </SalesCard>
    </Box>
  );
};

export default SalesExpenseNew;
